import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { announcementsService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const AnnouncementsListPage = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    show_as_popup: '',
    search: '',
    sortBy: 'priority',
    sortOrder: 'DESC'
  });

  useEffect(() => {
    loadAnnouncements();
  }, [pagination.page, pagination.limit, filters]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await announcementsService.getAllAnnouncements(params);

      if (response.success) {
        setAnnouncements(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Duyurular yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
      toast.error('Duyurular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId, title) => {
    if (!window.confirm(`"${title}" duyurusunu silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const response = await announcementsService.deleteAnnouncement(announcementId);
      if (response.success) {
        toast.success('Duyuru başarıyla silindi');
        loadAnnouncements();
      } else {
        toast.error(response.error || 'Duyuru silinemedi');
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Duyuru silinirken bir hata oluştu');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTypeBadge = (type) => {
    const badges = {
      info: 'bg-info',
      success: 'bg-success',
      warning: 'bg-warning',
      error: 'bg-danger'
    };
    return badges[type] || 'bg-secondary';
  };

  const getTypeLabel = (type) => {
    const labels = {
      info: 'Bilgi',
      success: 'Başarı',
      warning: 'Uyarı',
      error: 'Hata'
    };
    return labels[type] || type;
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Duyurular" />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Duyuru Listesi</h5>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/announcements/add')}
          >
            <Icon icon="solar:add-circle-outline" className="me-2" />
            Duyuru Ekle
          </button>
        </div>
        <div className="card-body">
          {/* Filtreler */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label">Tip</label>
              <select
                className="form-select"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="info">Bilgi</option>
                <option value="success">Başarı</option>
                <option value="warning">Uyarı</option>
                <option value="error">Hata</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Popup</label>
              <select
                className="form-select"
                value={filters.show_as_popup}
                onChange={(e) => handleFilterChange('show_as_popup', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="true">Popup Göster</option>
                <option value="false">Popup Gösterme</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Arama</label>
              <input
                type="text"
                className="form-control"
                placeholder="Başlık, içerik ara..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Sıralama</label>
              <select
                className="form-select"
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="priority_DESC">Öncelik (Yüksek → Düşük)</option>
                <option value="priority_ASC">Öncelik (Düşük → Yüksek)</option>
                <option value="created_at_DESC">Tarih (Yeni → Eski)</option>
                <option value="created_at_ASC">Tarih (Eski → Yeni)</option>
              </select>
            </div>
          </div>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : announcements.length === 0 ? (
            <div className="alert alert-info text-center">
              Duyuru bulunamadı.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Başlık</th>
                      <th>Tip</th>
                      <th>Öncelik</th>
                      <th>Popup</th>
                      <th>Başlangıç</th>
                      <th>Bitiş</th>
                      <th>Oluşturulma</th>
                      <th className="text-end">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map(announcement => (
                      <tr key={announcement.id}>
                        <td>
                          <strong>{announcement.title}</strong>
                        </td>
                        <td>
                          <span className={`badge ${getTypeBadge(announcement.type)}`}>
                            {getTypeLabel(announcement.type)}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-secondary">{announcement.priority}</span>
                        </td>
                        <td>
                          {announcement.show_as_popup ? (
                            <span className="badge bg-success">Evet</span>
                          ) : (
                            <span className="badge bg-secondary">Hayır</span>
                          )}
                        </td>
                        <td>{formatDate(announcement.start_date)}</td>
                        <td>{formatDate(announcement.end_date)}</td>
                        <td>{formatDate(announcement.created_at)}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => navigate(`/announcements/edit/${announcement.id}`)}
                          >
                            <Icon icon="solar:pen-outline" className="me-1" />
                            Düzenle
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(announcement.id, announcement.title)}
                          >
                            <Icon icon="solar:trash-bin-outline" className="me-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', display: 'inline-block' }}
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ms-2 text-muted">
                    Toplam {pagination.total} kayıt
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return (
                          <li key={page} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default AnnouncementsListPage;

